import axios from 'axios';
import { useAppContext } from '../utils/AppContext';
import { useEffect, useRef, useState } from 'react';

export const api_host = process.env.REACT_APP_API_HOST;

export const useFetchSessionInfo = () => {
  const context = useAppContext();
  // For now, we just want to run this on the first page load
  const hasRunRef = useRef(false);

  // login and logging states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [isStaff, setIsStaff] = useState<boolean | undefined>(false);
  const [isSuperuser, setIsSuperuser] = useState<boolean | undefined>(false);

  useEffect(() => {
    if (hasRunRef.current) return;

    const fetchSessionInfo = async () => {
      try {
        const response = await fetch(api_host + '/api/session-info', {
          // Important for session-based authentication
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();

          localStorage.setItem('authToken', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token);
          localStorage.setItem('csrfToken', data.csrf_token);

          // add staff and superuser to context
          context?.setUsername(data.user.username);
          setUsername(data.user.username);
          context?.setIsStaff(data.user.is_staff);
          setIsStaff(data.user.is_staff);
          context?.setIsSuperuser(data.user.is_superuser);
          setIsSuperuser(data.user.is_superuser);
          // set login and logging states
          setIsLoggedIn(true);
          setIsLoading(false);

          return;
        }
      } catch (error) { }

      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('csrfToken');
      context?.setUsername(undefined);
      setUsername(undefined);
      // remove staff and superuser from context
      context?.setIsStaff(undefined);
      setIsStaff(false);
      context?.setIsSuperuser(undefined);
      setIsSuperuser(false);
      // set login and logging states
      setIsLoggedIn(false);
      setIsLoading(false);
    };

    fetchSessionInfo();
    hasRunRef.current = true;
  }, [context]);

  // return login and logging states
  return { isLoggedIn, isLoading, username, isStaff, isSuperuser };

};

const apiClient = axios.create({
  baseURL: api_host,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const authToken = localStorage.getItem('authToken');
  const csrfToken = localStorage.getItem('csrfToken');

  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }

  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }

  return config;
});

// refreshing access token if expired
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // get pre-existing refresh_token
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken != null && error.response.data.code === 'token_not_valid') {
      try {
        // Refresh access token
        const response = await axios.post(api_host + '/api/refresh-token/', {
          refresh: refreshToken
        });

        // Update the new access token to localStorage
        localStorage.setItem('authToken', response.data.access);

        // Retry the original request with refreshed access token
        originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;

        return apiClient(originalRequest);

      } catch (refreshError) {

        console.error('Error refreshing token:', refreshError);

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
